import { useState, useEffect } from 'react';
// Link removed - using native <a> for external links
import { motion } from 'framer-motion';
import { 
  Disc3, Search, MapPin, Instagram, Music, Play, 
  Loader2, ExternalLink, DollarSign, Clock, Eye,
  Sparkles, Filter, Plane, ChevronDown, Star, Globe,
  Mail, Phone, Award, Building2, PartyPopper, FileText,
  Facebook, Twitter, Youtube, Headphones, Image, Heart
} from 'lucide-react';
import { toast } from 'sonner';
import ProducerLayout from '../../components/producer/ProducerLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const DJCard = ({ dj, onClick, isFavorite, onToggleFavorite }) => {
  // Get flag from nationality code
  const getFlag = (code) => {
    const flags = {
      'AR': '🇦🇷', 'BR': '🇧🇷', 'CL': '🇨🇱', 'CO': '🇨🇴', 'MX': '🇲🇽',
      'PE': '🇵🇪', 'UY': '🇺🇾', 'PY': '🇵🇾', 'EC': '🇪🇨', 'VE': '🇻🇪',
      'BO': '🇧🇴', 'ES': '🇪🇸', 'US': '🇺🇸', 'GB': '🇬🇧', 'DE': '🇩🇪',
      'FR': '🇫🇷', 'IT': '🇮🇹', 'NL': '🇳🇱', 'BE': '🇧🇪', 'PT': '🇵🇹',
      'JP': '🇯🇵', 'KR': '🇰🇷', 'AU': '🇦🇺', 'CA': '🇨🇦', 'ZA': '🇿🇦',
      'IL': '🇮🇱', 'SE': '🇸🇪', 'OTHER': '🌎'
    };
    return flags[code] || null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02 }}
      className="bg-[#121212] border border-[#27272A] rounded-2xl overflow-hidden cursor-pointer hover:border-violet-500/30 transition-all group"
    >
      {/* Banner/Photo */}
      <div className="h-32 bg-gradient-to-br from-violet-500/20 to-transparent relative" onClick={onClick}>
        {dj.banner_url && (
          <img src={dj.banner_url} alt="" className="w-full h-full object-cover" />
        )}
        <div className="absolute -bottom-8 left-5">
          <div className="w-16 h-16 rounded-xl border-4 border-[#121212] overflow-hidden bg-[#27272A] relative">
            {dj.photo_url ? (
              <img src={dj.photo_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Disc3 size={24} className="text-violet-500" />
              </div>
            )}
            {/* Nationality Flag Badge */}
            {dj.nationality && getFlag(dj.nationality) && (
              <div 
                className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-black/80 flex items-center justify-center text-sm shadow-lg border border-zinc-700"
                title={dj.nationality_name || dj.nationality}
              >
                {getFlag(dj.nationality)}
              </div>
            )}
          </div>
        </div>
        {dj.featured && (
          <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-violet-500 rounded text-black text-xs font-bold">
            <Star size={12} /> Destacado
          </div>
        )}
        {/* Favorite button */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleFavorite(dj.id); }}
          className={`absolute top-3 ${dj.featured ? 'right-24' : 'right-3'} p-2 rounded-lg transition-all ${
            isFavorite 
              ? 'bg-red-500 text-white' 
              : 'bg-black/50 text-white/60 hover:text-white hover:bg-black/70'
          }`}
        >
          <Heart size={16} fill={isFavorite ? 'currentColor' : 'none'} />
        </button>
      </div>
      
      <div className="pt-10 pb-5 px-5" onClick={onClick}>
        <h3 className="font-bold text-lg mb-1">{dj.artistic_name}</h3>
        <p className="text-sm text-zinc-400 flex items-center gap-1 mb-3">
          <MapPin size={12} /> {dj.city}
          {dj.management?.name && (
            <span className="text-purple-400 ml-2 flex items-center gap-1">
              <Building2 size={12} /> {dj.management.name}
            </span>
          )}
        </p>
        
        {/* Genres */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {(dj.genres || []).slice(0, 3).map(genre => (
            <span key={genre} className="px-2 py-0.5 bg-violet-500/10 text-violet-500 rounded text-xs">
              {genre}
            </span>
          ))}
          {(dj.genres || []).length > 3 && (
            <span className="px-2 py-0.5 text-zinc-500 text-xs">+{dj.genres.length - 3}</span>
          )}
        </div>
        
        {/* Quick Stats */}
        <div className="flex items-center justify-between text-xs text-zinc-500 pt-3 border-t border-[#27272A]">
          <span className="flex items-center gap-1">
            <Clock size={12} /> {dj.experience_years || '?'} años exp.
          </span>
          {dj.cache_min && (
            <span className="flex items-center gap-1">
              <DollarSign size={12} /> Desde ${dj.cache_min?.toLocaleString()}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Eye size={12} /> {dj.profile_views || 0}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

const ProducerDJsPage = () => {
  const [loading, setLoading] = useState(true);
  const [djs, setDjs] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ genre: '', city: '', search: '' });
  const [favorites, setFavorites] = useState(new Set());
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [availableFilters, setAvailableFilters] = useState({ genres: [], cities: [] });
  const [selectedDJ, setSelectedDJ] = useState(null);

  useEffect(() => {
    fetchDJs();
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      const response = await api.get('/dj/favorites');
      const favIds = (response.data.favorites || []).map(f => f.dj_id);
      setFavorites(new Set(favIds));
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  const toggleFavorite = async (djId) => {
    const isFav = favorites.has(djId);
    try {
      if (isFav) {
        await api.delete(`/dj/favorites/${djId}`);
        setFavorites(prev => {
          const next = new Set(prev);
          next.delete(djId);
          return next;
        });
        toast.success('DJ eliminado de favoritos');
      } else {
        await api.post(`/dj/favorites/${djId}`);
        setFavorites(prev => new Set([...prev, djId]));
        toast.success('DJ agregado a favoritos');
      }
    } catch (error) {
      toast.error('Error al actualizar favoritos');
    }
  };

  const fetchDJs = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.genre) params.append('genre', filters.genre);
      if (filters.city) params.append('city', filters.city);
      if (filters.search) params.append('search', filters.search);
      
      const response = await api.get(`/dj/browse?${params.toString()}`);
      setDjs(response.data.djs);
      setTotal(response.data.total);
      setAvailableFilters(response.data.filters);
    } catch (error) {
      console.error('Error fetching DJs:', error);
      toast.error('Error al cargar DJs');
    } finally {
      setLoading(false);
    }
  };

  const fetchDJProfile = async (djId) => {
    try {
      const response = await api.get(`/dj/profile/${djId}`);
      setSelectedDJ(response.data);
    } catch (error) {
      toast.error('Error al cargar perfil');
    }
  };

  return (
    <SidebarProvider>
      <ProducerLayout>
        <div className="p-6 lg:p-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center">
                <Disc3 size={28} className="text-black" />
              </div>
              <div>
                <h1 className="text-2xl font-black">DJs Sugeridos</h1>
                <p className="text-zinc-500 text-sm">{total} DJs disponibles para tus eventos</p>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-[#121212] border border-[#27272A] rounded-xl p-5 mb-6">
            <div className="grid md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <Input
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                  placeholder="Buscar por nombre o estilo..."
                  className="bg-[#0A0A0A] border-[#27272A]"
                />
              </div>
              <Select value={filters.genre || "all"} onValueChange={(v) => setFilters({...filters, genre: v === "all" ? "" : v})}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]">
                  <SelectValue placeholder="Género" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los géneros</SelectItem>
                  {availableFilters.genres.map(g => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={fetchDJs} className="bg-gradient-to-r from-violet-600 to-pink-600 text-white">
                <Search size={16} className="mr-2" /> Buscar
              </Button>
              <Button 
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                variant={showFavoritesOnly ? "default" : "outline"}
                className={showFavoritesOnly ? "bg-red-500 hover:bg-red-600" : "border-[#27272A]"}
              >
                <Heart size={16} className="mr-2" fill={showFavoritesOnly ? "white" : "none"} />
                {showFavoritesOnly ? 'Ver todos' : 'Favoritos'}
              </Button>
            </div>
          </div>

          {/* DJs Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={40} className="animate-spin text-violet-500" />
            </div>
          ) : djs.length === 0 ? (
            <div className="text-center py-20">
              <Disc3 size={48} className="mx-auto mb-4 text-zinc-600" />
              <h3 className="text-xl font-bold mb-2">No hay DJs disponibles</h3>
              <p className="text-zinc-500">Probá ajustando los filtros de búsqueda</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {djs
                .filter(dj => !showFavoritesOnly || favorites.has(dj.id))
                .map(dj => (
                <DJCard 
                  key={dj.id} 
                  dj={dj} 
                  onClick={() => fetchDJProfile(dj.id)}
                  isFavorite={favorites.has(dj.id)}
                  onToggleFavorite={toggleFavorite}
                />
              ))}
            </div>
          )}

          {/* DJ Detail Modal */}
          <Dialog open={!!selectedDJ} onOpenChange={() => setSelectedDJ(null)}>
            <DialogContent className="bg-[#0A0A0A] border-[#27272A] max-w-4xl max-h-[90vh] overflow-y-auto">
              {selectedDJ && (
                <>
                  {/* Header */}
                  <div className="relative h-40 -mx-6 -mt-6 mb-12 bg-gradient-to-br from-violet-500/30 to-transparent">
                    {selectedDJ.banner_url && (
                      <img src={selectedDJ.banner_url} alt="" className="w-full h-full object-cover" />
                    )}
                    <div className="absolute -bottom-10 left-6 flex items-end gap-4">
                      <div className="w-24 h-24 rounded-2xl border-4 border-[#0A0A0A] overflow-hidden bg-[#27272A]">
                        {selectedDJ.photo_url ? (
                          <img src={selectedDJ.photo_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Disc3 size={40} className="text-violet-500" />
                          </div>
                        )}
                      </div>
                      <div className="pb-2">
                        <h2 className="text-2xl font-black flex items-center gap-2">
                          {selectedDJ.artistic_name}
                          {/* Nationality flag */}
                          {selectedDJ.nationality && (
                            <span className="text-xl" title={selectedDJ.nationality_name || selectedDJ.nationality}>
                              {(() => {
                                const flags = {
                                  'AR': '🇦🇷', 'BR': '🇧🇷', 'CL': '🇨🇱', 'CO': '🇨🇴', 'MX': '🇲🇽',
                                  'PE': '🇵🇪', 'UY': '🇺🇾', 'PY': '🇵🇾', 'EC': '🇪🇨', 'VE': '🇻🇪',
                                  'BO': '🇧🇴', 'ES': '🇪🇸', 'US': '🇺🇸', 'GB': '🇬🇧', 'DE': '🇩🇪',
                                  'FR': '🇫🇷', 'IT': '🇮🇹', 'NL': '🇳🇱', 'BE': '🇧🇪', 'PT': '🇵🇹',
                                  'JP': '🇯🇵', 'KR': '🇰🇷', 'AU': '🇦🇺', 'CA': '🇨🇦', 'ZA': '🇿🇦',
                                  'IL': '🇮🇱', 'SE': '🇸🇪', 'OTHER': '🌎'
                                };
                                return flags[selectedDJ.nationality] || '🌎';
                              })()}
                            </span>
                          )}
                        </h2>
                        <p className="text-zinc-400 flex items-center gap-1">
                          <MapPin size={14} /> {selectedDJ.city}
                        </p>
                        {/* Management info */}
                        {selectedDJ.management?.name && (
                          <div className="flex items-center gap-2 mt-2 text-sm">
                            <Building2 size={14} className="text-purple-400" />
                            <span className="text-purple-400 font-medium">{selectedDJ.management.name}</span>
                            {selectedDJ.management.type && (
                              <span className="text-zinc-500">
                                ({selectedDJ.management.type === 'record_label' ? 'Sello' : 
                                  selectedDJ.management.type === 'agency' ? 'Agencia' :
                                  selectedDJ.management.type === 'manager' ? 'Manager' :
                                  selectedDJ.management.type === 'production' ? 'Productora' :
                                  selectedDJ.management.type === 'collective' ? 'Colectivo' : 'Independiente'})
                              </span>
                            )}
                            {selectedDJ.management.website && (
                              <a 
                                href={selectedDJ.management.website} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-zinc-500 hover:text-purple-400"
                              >
                                <ExternalLink size={12} />
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    {selectedDJ.featured && (
                      <div className="absolute top-3 right-3 flex items-center gap-1 px-3 py-1.5 bg-violet-500 rounded-lg text-black text-sm font-bold">
                        <Star size={14} /> Destacado
                      </div>
                    )}
                  </div>

                  <div className="space-y-6">
                    {/* Genres */}
                    <div className="flex flex-wrap gap-2">
                      {(selectedDJ.genres || []).map(genre => (
                        <span key={genre} className="px-3 py-1.5 bg-violet-500/10 text-violet-500 rounded-full text-sm font-medium">
                          {genre}
                        </span>
                      ))}
                    </div>

                    {/* Bio */}
                    <div>
                      <h4 className="font-bold mb-2 flex items-center gap-2">
                        <Music size={16} className="text-violet-500" /> Biografía
                      </h4>
                      <p className="text-zinc-400 whitespace-pre-wrap">{selectedDJ.bio}</p>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {selectedDJ.experience_years && (
                        <div className="bg-[#121212] rounded-xl p-4 text-center">
                          <p className="text-2xl font-black text-violet-500">{selectedDJ.experience_years}</p>
                          <p className="text-xs text-zinc-500">Años de experiencia</p>
                        </div>
                      )}
                      {selectedDJ.cache_min && (
                        <div className="bg-[#121212] rounded-xl p-4 text-center">
                          <p className="text-2xl font-black text-violet-500">${selectedDJ.cache_min?.toLocaleString()}</p>
                          <p className="text-xs text-zinc-500">Caché desde</p>
                        </div>
                      )}
                      {selectedDJ.cache_max && (
                        <div className="bg-[#121212] rounded-xl p-4 text-center">
                          <p className="text-2xl font-black text-violet-500">${selectedDJ.cache_max?.toLocaleString()}</p>
                          <p className="text-xs text-zinc-500">Caché hasta</p>
                        </div>
                      )}
                      <div className="bg-[#121212] rounded-xl p-4 text-center">
                        <p className="text-2xl font-black text-violet-500">{selectedDJ.profile_views || 0}</p>
                        <p className="text-xs text-zinc-500">Vistas del perfil</p>
                      </div>
                      {selectedDJ.travel_available && (
                        <div className="bg-[#121212] rounded-xl p-4 text-center flex flex-col items-center justify-center">
                          <Plane size={24} className="text-violet-500 mb-1" />
                          <p className="text-xs text-zinc-500">Viaja (nacional)</p>
                        </div>
                      )}
                      {selectedDJ.international_available && (
                        <div className="bg-[#121212] rounded-xl p-4 text-center flex flex-col items-center justify-center">
                          <Globe size={24} className="text-emerald-400 mb-1" />
                          <p className="text-xs text-zinc-500">Internacional</p>
                        </div>
                      )}
                      {selectedDJ.performance_style && (
                        <div className="bg-[#121212] rounded-xl p-4 text-center">
                          <p className="text-sm font-bold text-violet-500 capitalize">{selectedDJ.performance_style}</p>
                          <p className="text-xs text-zinc-500">Estilo</p>
                        </div>
                      )}
                      {selectedDJ.set_duration_min && selectedDJ.set_duration_max && (
                        <div className="bg-[#121212] rounded-xl p-4 text-center">
                          <p className="text-sm font-bold text-violet-500">{selectedDJ.set_duration_min}-{selectedDJ.set_duration_max} min</p>
                          <p className="text-xs text-zinc-500">Duración set</p>
                        </div>
                      )}
                    </div>

                    {/* Career Highlights */}
                    {(selectedDJ.residencies?.length > 0 || selectedDJ.festivals?.length > 0 || selectedDJ.labels?.length > 0) && (
                      <div className="grid md:grid-cols-3 gap-4">
                        {selectedDJ.residencies?.length > 0 && (
                          <div className="bg-[#121212] rounded-xl p-4">
                            <h5 className="font-bold text-sm mb-2 flex items-center gap-2">
                              <Building2 size={14} className="text-violet-500" /> Residencias
                            </h5>
                            <div className="flex flex-wrap gap-1">
                              {selectedDJ.residencies.map((r, i) => (
                                <span key={i} className="text-xs px-2 py-1 bg-[#1A1A1A] rounded">{r}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {selectedDJ.festivals?.length > 0 && (
                          <div className="bg-[#121212] rounded-xl p-4">
                            <h5 className="font-bold text-sm mb-2 flex items-center gap-2">
                              <PartyPopper size={14} className="text-violet-500" /> Festivales
                            </h5>
                            <div className="flex flex-wrap gap-1">
                              {selectedDJ.festivals.map((f, i) => (
                                <span key={i} className="text-xs px-2 py-1 bg-[#1A1A1A] rounded">{f}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {selectedDJ.labels?.length > 0 && (
                          <div className="bg-[#121212] rounded-xl p-4">
                            <h5 className="font-bold text-sm mb-2 flex items-center gap-2">
                              <Disc3 size={14} className="text-violet-500" /> Sellos
                            </h5>
                            <div className="flex flex-wrap gap-1">
                              {selectedDJ.labels.map((l, i) => (
                                <span key={i} className="text-xs px-2 py-1 bg-[#1A1A1A] rounded">{l}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Awards */}
                    {selectedDJ.awards?.length > 0 && (
                      <div>
                        <h4 className="font-bold mb-2 flex items-center gap-2">
                          <Award size={16} className="text-violet-500" /> Premios y Reconocimientos
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedDJ.awards.map((award, i) => (
                            <span key={i} className="text-sm px-3 py-1.5 bg-violet-500/10 text-violet-500 rounded-lg">{award}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Releases */}
                    {selectedDJ.releases?.length > 0 && (
                      <div>
                        <h4 className="font-bold mb-3 flex items-center gap-2">
                          <Headphones size={16} className="text-violet-500" /> Releases
                        </h4>
                        <div className="grid md:grid-cols-2 gap-2">
                          {selectedDJ.releases.map((release, i) => (
                            <div key={i} className="flex items-center gap-3 p-3 bg-[#121212] rounded-lg">
                              <Music size={16} className="text-violet-500" />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{release.title}</p>
                                <p className="text-xs text-zinc-500">{release.year} {release.label && `• ${release.label}`}</p>
                              </div>
                              {release.link && (
                                <a href={release.link} target="_blank" rel="noopener noreferrer" className="text-violet-500">
                                  <ExternalLink size={14} />
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Photo Gallery */}
                    {selectedDJ.gallery_urls?.length > 0 && (
                      <div>
                        <h4 className="font-bold mb-3 flex items-center gap-2">
                          <Image size={16} className="text-violet-500" /> Galería
                        </h4>
                        <div className="grid grid-cols-3 gap-2">
                          {selectedDJ.gallery_urls.slice(0, 6).map((url, i) => (
                            <img key={i} src={url} alt="" className="w-full aspect-square object-cover rounded-lg" />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Social & Music Links */}
                    <div>
                      <h4 className="font-bold mb-3">Redes y Plataformas</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedDJ.instagram && (
                          <a href={`https://instagram.com/${selectedDJ.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer"
                             className="flex items-center gap-2 px-3 py-2 bg-[#121212] rounded-lg hover:bg-[#1A1A1A] transition-colors text-sm">
                            <Instagram size={16} className="text-pink-500" /> Instagram
                          </a>
                        )}
                        {selectedDJ.facebook && (
                          <a href={selectedDJ.facebook} target="_blank" rel="noopener noreferrer"
                             className="flex items-center gap-2 px-3 py-2 bg-[#121212] rounded-lg hover:bg-[#1A1A1A] transition-colors text-sm">
                            <Facebook size={16} className="text-blue-500" /> Facebook
                          </a>
                        )}
                        {selectedDJ.twitter && (
                          <a href={`https://twitter.com/${selectedDJ.twitter.replace('@', '')}`} target="_blank" rel="noopener noreferrer"
                             className="flex items-center gap-2 px-3 py-2 bg-[#121212] rounded-lg hover:bg-[#1A1A1A] transition-colors text-sm">
                            <Twitter size={16} className="text-sky-400" /> Twitter
                          </a>
                        )}
                        {selectedDJ.tiktok && (
                          <a href={`https://tiktok.com/${selectedDJ.tiktok.replace('@', '')}`} target="_blank" rel="noopener noreferrer"
                             className="flex items-center gap-2 px-3 py-2 bg-[#121212] rounded-lg hover:bg-[#1A1A1A] transition-colors text-sm">
                            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" style={{color: '#ff0050'}}>
                              <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                            </svg>
                            TikTok
                          </a>
                        )}
                        {selectedDJ.youtube && (
                          <a href={selectedDJ.youtube} target="_blank" rel="noopener noreferrer"
                             className="flex items-center gap-2 px-3 py-2 bg-[#121212] rounded-lg hover:bg-[#1A1A1A] transition-colors text-sm">
                            <Youtube size={16} className="text-red-500" /> YouTube
                          </a>
                        )}
                        {selectedDJ.spotify_url && (
                          <a href={selectedDJ.spotify_url} target="_blank" rel="noopener noreferrer"
                             className="flex items-center gap-2 px-3 py-2 bg-[#121212] rounded-lg hover:bg-[#1A1A1A] transition-colors text-sm">
                            <Music size={16} className="text-green-500" /> Spotify
                          </a>
                        )}
                        {selectedDJ.soundcloud_url && (
                          <a href={selectedDJ.soundcloud_url} target="_blank" rel="noopener noreferrer"
                             className="flex items-center gap-2 px-3 py-2 bg-[#121212] rounded-lg hover:bg-[#1A1A1A] transition-colors text-sm">
                            <Play size={16} className="text-orange-500" /> SoundCloud
                          </a>
                        )}
                        {selectedDJ.mixcloud_url && (
                          <a href={selectedDJ.mixcloud_url} target="_blank" rel="noopener noreferrer"
                             className="flex items-center gap-2 px-3 py-2 bg-[#121212] rounded-lg hover:bg-[#1A1A1A] transition-colors text-sm">
                            <Headphones size={16} className="text-cyan-400" /> Mixcloud
                          </a>
                        )}
                        {selectedDJ.beatport_url && (
                          <a href={selectedDJ.beatport_url} target="_blank" rel="noopener noreferrer"
                             className="flex items-center gap-2 px-3 py-2 bg-[#121212] rounded-lg hover:bg-[#1A1A1A] transition-colors text-sm">
                            <Music size={16} className="text-lime-400" /> Beatport
                          </a>
                        )}
                        {selectedDJ.website && (
                          <a href={selectedDJ.website} target="_blank" rel="noopener noreferrer"
                             className="flex items-center gap-2 px-3 py-2 bg-[#121212] rounded-lg hover:bg-[#1A1A1A] transition-colors text-sm">
                            <Globe size={16} className="text-violet-500" /> Website
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Videos */}
                    {selectedDJ.video_urls?.length > 0 && (
                      <div>
                        <h4 className="font-bold mb-3 flex items-center gap-2">
                          <Play size={16} className="text-violet-500" /> Videos
                        </h4>
                        <div className="space-y-2">
                          {selectedDJ.video_urls.map((url, idx) => (
                            <a key={idx} href={url} target="_blank" rel="noopener noreferrer"
                               className="flex items-center gap-2 p-3 bg-[#121212] rounded-lg hover:bg-[#1A1A1A] transition-colors">
                              <ExternalLink size={14} className="text-violet-500" />
                              <span className="text-sm truncate">{url}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Technical Rider */}
                    {(selectedDJ.technical_rider || selectedDJ.rider_pdf_url) && (
                      <div>
                        <h4 className="font-bold mb-2 flex items-center gap-2">
                          <FileText size={16} className="text-violet-500" /> Rider Técnico
                        </h4>
                        {selectedDJ.rider_pdf_url && (
                          <a href={selectedDJ.rider_pdf_url} target="_blank" rel="noopener noreferrer"
                             className="inline-flex items-center gap-2 px-4 py-2 mb-3 bg-violet-500/10 text-violet-500 rounded-lg hover:bg-violet-500/20 transition-colors">
                            <FileText size={16} /> Ver Rider PDF
                          </a>
                        )}
                        {selectedDJ.technical_rider && (
                          <div className="p-4 bg-[#121212] rounded-lg">
                            <p className="text-zinc-400 text-sm whitespace-pre-wrap">{selectedDJ.technical_rider}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Equipment */}
                    {selectedDJ.equipment_own && selectedDJ.equipment_details && (
                      <div>
                        <h4 className="font-bold mb-2">Equipo Propio</h4>
                        <div className="p-4 bg-[#121212] rounded-lg">
                          <p className="text-zinc-400 text-sm whitespace-pre-wrap">{selectedDJ.equipment_details}</p>
                        </div>
                      </div>
                    )}

                    {/* Special Skills */}
                    {selectedDJ.special_skills?.length > 0 && (
                      <div>
                        <h4 className="font-bold mb-2">Habilidades Especiales</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedDJ.special_skills.map((skill, i) => (
                            <span key={i} className="px-3 py-1.5 bg-purple-500/10 text-purple-400 rounded-full text-sm">{skill}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Contact */}
                    <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-5">
                      <h4 className="font-bold mb-3 text-center">Contactar a {selectedDJ.artistic_name}</h4>
                      <div className="flex flex-wrap justify-center gap-3">
                        {selectedDJ.email_contact && (
                          <a href={`mailto:${selectedDJ.email_contact}`}
                             className="flex items-center gap-2 px-4 py-2 bg-violet-500 text-black rounded-lg font-medium hover:bg-pink-500 transition-colors">
                            <Mail size={16} /> {selectedDJ.email_contact}
                          </a>
                        )}
                        {selectedDJ.phone && (
                          <a href={`tel:${selectedDJ.phone}`}
                             className="flex items-center gap-2 px-4 py-2 bg-[#121212] rounded-lg hover:bg-[#1A1A1A] transition-colors">
                            <Phone size={16} className="text-violet-500" /> {selectedDJ.phone}
                          </a>
                        )}
                        {!selectedDJ.email_contact && !selectedDJ.phone && (
                          <p className="text-zinc-400 text-sm">Contactalo a través de sus redes sociales</p>
                        )}
                      </div>
                      {/* Link to public profile */}
                      <div className="mt-4 pt-4 border-t border-violet-500/20 text-center">
                        <a
                          href={`/dj/${selectedDJ.slug || selectedDJ.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white hover:bg-white/10 transition-colors"
                        >
                          <ExternalLink size={16} /> Ver Press Kit Público
                        </a>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </ProducerLayout>
    </SidebarProvider>
  );
};

export default ProducerDJsPage;
